import Model, { attr } from '@ember-data/model';
import { cached } from '@glimmer/tracking';

const DISCOUNTABLE_CANCELLATION_POLICIES = [
  'CANCEL_FLEXIBLE',
  'CANCEL_BETTER_STRICT_WITH_GRACE_PERIOD',
  'CANCEL_STRICT_14_WITH_GRACE_PERIOD',
  'CANCEL_MODERATE',
];

export type CancellationPolicyId =
  | 'CANCEL_FLEXIBLE'
  | 'CANCEL_MODERATE'
  | 'CANCEL_BETTER_STRICT_WITH_GRACE_PERIOD'
  | 'CANCEL_STRICT_14_WITH_GRACE_PERIOD'
  | 'CANCEL_SUPER_STRICT_30'
  | 'CANCEL_SUPER_STRICT_60'
  | 'CANCEL_FIRM_14';

type NewListingPromotion = 'available' | 'ongoing' | 'expired';

export const INSTANT_BOOK_WELCOME_MESSAGE_MAX_LENGTH = 400;
export const LISTING_EXPECTATION_DETAIL_MAX_LENGTH = 300;

export const LISTING_EXPECTATION_TYPES = [
  'requires_stairs',
  'potential_noise',
  'has_pets',
  'limited_parking',
  'shared_spaces',
  'limited_amenities',
  'surveillance',
  'noise_monitor',
  'weapons',
  'animals',
  'pool_or_jacuzzi_with_no_fence',
  'lake_or_river_or_water_body',
  'climbing_or_play_structure',
  'heights_with_no_fence',
] as const;

export const CHECK_IN_CATEGORIES = [
  'doorman_entry',
  'lockbox',
  'smartlock',
  'keypad',
  'host_checkin',
  'other_checkin',
] as const;

export type CheckInCategory = (typeof CHECK_IN_CATEGORIES)[number];

export interface ListingExpectation {
  type: string;
  added_details?: string;
}

export type BlackoutDatesAttribute =
  | 'mobileOnlyDiscountBlackoutDates'
  | 'highRatedGuestDiscountBlackoutDates';

export default class AirbnbOfficialListingModel extends Model {
  @attr('string') externalId!: string;
  @attr('string') name!: string;
  @attr('string') nickname!: string;
  @attr('number') nonRefundableDiscount!: number;
  @attr('array') eligibleCancellationPolicyIds!: CancellationPolicyId[];
  @attr('string') configuredCancellationPolicyId!: CancellationPolicyId;
  @attr('string') newListingPromotion?: NewListingPromotion;
  @attr('boolean') mobileOnlyDiscountEnabled!: boolean;
  @attr('boolean') highRatedGuestDiscountEnabled!: boolean;
  @attr('string') instantBookWelcomeMessage!: string | null;
  @attr('array') listingExpectationsForGuests!: ListingExpectation[];
  @attr('string') checkInCategory!: CheckInCategory | null;
  @attr('string') checkInInstruction!: string | null;
  @attr('array') mobileOnlyDiscountBlackoutDates!: string[];
  @attr('array') highRatedGuestDiscountBlackoutDates!: string[];

  @cached
  get isCancellationPolicyDiscountable(): boolean {
    return DISCOUNTABLE_CANCELLATION_POLICIES.includes(
      this.configuredCancellationPolicyId,
    );
  }
}

declare module 'ember-data/types/registries/model' {
  interface ModelRegistry {
    'airbnb-official-listing': AirbnbOfficialListingModel;
  }
}
